import { useState, useEffect } from 'react';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { useUploadAudio } from '../hooks/useApi';
import { WaveformVisualizer } from './WaveformVisualizer';

interface AudioRecorderProps {
  onUploadSuccess?: (entryId: string) => void;
}

export function AudioRecorder({ onUploadSuccess }: AudioRecorderProps) {
  const {
    isRecording,
    isPaused,
    isTesting,
    duration,
    error,
    isSupported,
    audioContext,
    analyser,
    startTesting,
    stopTesting,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
  } = useAudioRecorder();

  const uploadMutation = useUploadAudio();
  const [isUploading, setIsUploading] = useState(false);

  // Auto-start mic test on page load (Option C: Smart Permission Detection)
  useEffect(() => {
    const autoStartTest = async () => {
      // Only auto-start if not already testing or recording
      if (isTesting || isRecording) return;

      try {
        // Check if mic permission already granted
        const result = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });

        if (result.state === 'granted') {
          // Auto-start test mode since we already have permission
          await startTesting();
        }
        // Otherwise wait for user to click "Test Mic" button
      } catch (err) {
        // Permissions API not supported (Safari, older browsers)
        // Don't auto-start, wait for user action
        console.log('Permissions API not supported, waiting for user action');
      }
    };

    autoStartTest();
  }, []); // Only run on mount

  const handleTestMic = async () => {
    if (isTesting) {
      stopTesting();
    } else {
      await startTesting();
    }
  };

  const handleStart = async () => {
    await startRecording();
  };

  const handlePauseResume = () => {
    if (isPaused) {
      resumeRecording();
    } else {
      pauseRecording();
    }
  };

  const handleStop = async () => {
    const result = await stopRecording();

    if (result) {
      setIsUploading(true);

      try {
        const response = await uploadMutation.mutateAsync({
          blob: result.blob,
          metadata: {
            duration: result.duration,
          },
        });

        onUploadSuccess?.(response.entryId);
      } catch (err) {
        console.error('Upload failed:', err);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleCancel = () => {
    if (confirm('Are you sure you want to discard this recording?')) {
      cancelRecording();
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isSupported) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">
          Audio recording is not supported in your browser. Please use a modern browser like
          Chrome, Firefox, or Edge.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-6 p-6 bg-white rounded-lg shadow">
      {error && (
        <div className="w-full p-3 bg-red-50 border border-red-200 rounded text-red-800">
          {error}
        </div>
      )}

      {uploadMutation.isError && (
        <div className="w-full p-3 bg-red-50 border border-red-200 rounded text-red-800">
          Upload failed: {uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Unknown error'}
        </div>
      )}

      {/* Waveform Visualizer */}
      {(isTesting || isRecording) && (
        <WaveformVisualizer
          audioContext={audioContext}
          analyser={analyser}
          isActive={isTesting || (isRecording && !isPaused)}
          isRecording={isRecording && !isPaused}
        />
      )}

      {/* Duration Display */}
      {isRecording && (
        <div className="text-6xl font-mono text-gray-800">
          {formatTime(duration)}
        </div>
      )}

      {/* Control Buttons */}
      <div className="flex flex-wrap items-center justify-center gap-4">
        {/* Mic Test Button - only show when not recording */}
        {!isRecording && !isUploading && (
          <button
            onClick={handleTestMic}
            className={`px-8 py-4 rounded-full transition-colors flex items-center space-x-2 ${
              isTesting
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-gray-500 text-white hover:bg-gray-600'
            }`}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
              />
            </svg>
            <span>{isTesting ? 'Stop Test' : 'Test Mic'}</span>
          </button>
        )}

        {/* Start/Stop Recording */}
        {!isRecording ? (
          <button
            onClick={handleStart}
            disabled={isUploading}
            className="px-8 py-4 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z"
                clipRule="evenodd"
              />
            </svg>
            <span>Start Recording</span>
          </button>
        ) : (
          <>
            {/* Pause/Resume Button */}
            <button
              onClick={handlePauseResume}
              className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center space-x-2"
            >
              {isPaused ? (
                <>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Resume</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Pause</span>
                </>
              )}
            </button>

            {/* Stop Button */}
            <button
              onClick={handleStop}
              className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Stop & Upload</span>
            </button>

            {/* Cancel Button */}
            <button
              onClick={handleCancel}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <span>Cancel</span>
            </button>
          </>
        )}
      </div>

      {/* Status Indicators */}
      {isRecording && !isPaused && (
        <div className="flex items-center space-x-2 text-red-600">
          <span className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></span>
          <span className="text-sm font-medium">Recording...</span>
        </div>
      )}

      {isPaused && (
        <div className="flex items-center space-x-2 text-yellow-600">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-medium">Paused</span>
        </div>
      )}

      {isUploading && (
        <div className="flex items-center space-x-2 text-blue-600">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-sm font-medium">Uploading...</span>
        </div>
      )}
    </div>
  );
}
